import { Link } from 'react-router-dom';
import { 
  UserPlus, 
  Upload, 
  BadgeCheck, 
  Eye, 
  FileText, 
  Clock, 
  Bell, 
  Shield,
  ArrowRight,
  CheckCircle2,
  GraduationCap,
  Briefcase,
  Award,
  Github,
  Youtube,
  Linkedin as LinkedinIcon,
  Globe,
  Download
} from 'lucide-react';
import { Button } from '../components/ui/button';

const STUDENT_PROFILE = "https://images.unsplash.com/photo-1765648684630-ac9c15ac98d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIwYnVzaW5lc3MlMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzMyNDc0NzB8MA&ixlib=rb-4.1.0&q=85";

const features = [
  {
    icon: FileText,
    title: 'Structured Profiles',
    description: 'Create comprehensive profiles capturing academic outputs, practical experience, skills, and training.',
  },
  {
    icon: Upload,
    title: 'Portfolio Links',
    description: 'Link your portfolios from GitHub, YouTube, Behance, LinkedIn, Google Drive, ORCID and more to showcase your work.',
  },
  {
    icon: BadgeCheck,
    title: 'Referee Verification',
    description: 'Invite referees to verify your achievements, adding credibility to your profile.',
  },
  {
    icon: Eye,
    title: 'Discoverability',
    description: 'Become visible to organizations seeking candidates with your specific skills and experience.',
  },
  {
    icon: Download,
    title: 'PDF Export',
    description: 'Download your profile as a professional A4 PDF to share with potential employers directly.',
  },
  {
    icon: Bell,
    title: 'Smart Reminders',
    description: 'Receive reminders to review, update, and maintain your profile.',
  },
];

const portfolioPlatforms = [
  { name: 'GitHub', icon: Github, color: 'bg-gray-800' },
  { name: 'YouTube', icon: Youtube, color: 'bg-red-600' },
  { name: 'LinkedIn', icon: LinkedinIcon, color: 'bg-blue-600' },
  { name: 'Behance', icon: Globe, color: 'bg-blue-500' },
  { name: 'Google Drive', icon: Globe, color: 'bg-green-600' },
  { name: 'ORCID', icon: Globe, color: 'bg-lime-600' },
];

const targetAudience = [
  {
    icon: GraduationCap,
    title: 'Students',
    description: 'Final-year students preparing to enter the professional world.',
  },
  {
    icon: Award,
    title: 'Recent Graduates',
    description: 'Recent graduates looking to showcase their achievements.',
  },
  {
    icon: Briefcase,
    title: 'Early-Career Professionals',
    description: 'Professionals building their career with verified credentials.',
  },
];

export default function Candidates() {
  return (
    <div data-testid="candidates-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-meritum-navy via-meritum-teal to-meritum-teal">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="text-white">
              <span className="inline-block px-4 py-2 bg-white/10 border border-white/20 rounded-sm text-meritum-gold font-body text-sm font-medium mb-6">
                For Candidates
              </span>
              <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight leading-none mb-6">
                Build Your Verified
                <span className="block text-meritum-gold">Professional Profile</span>
              </h1>
              <p className="font-body text-lg md:text-xl text-slate-200 leading-relaxed mb-8">
                Create a dynamic professional profile that grows with your achievements. 
                Link your portfolios, get verified, and unlock new professional opportunities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/pricing">
                  <Button 
                    data-testid="candidates-create-profile-btn"
                    className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold px-8 py-6 text-base"
                  >
                    Create Your Profile
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button 
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-meritum-navy font-body font-medium px-8 py-6 text-base"
                  >
                    View Pricing
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src={STUDENT_PROFILE}
                alt="Professional candidate"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Links Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 bg-meritum-gold/10 text-meritum-gold font-body text-sm font-medium rounded-sm mb-4">
              Showcase Your Work
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-meritum-navy tracking-tight mb-4">
              Link Your Portfolios
            </h2>
            <p className="font-body text-slate-600 max-w-2xl mx-auto">
              Connect your professional portfolios and showcase your work to potential employers.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {portfolioPlatforms.map((platform, index) => (
              <div
                key={index}
                className="flex items-center gap-3 px-6 py-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className={`w-8 h-8 ${platform.color} rounded flex items-center justify-center`}>
                  <platform.icon size={16} className="text-white" />
                </div>
                <span className="font-body text-sm font-medium text-meritum-navy">{platform.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 bg-meritum-gold/10 text-meritum-gold font-body text-sm font-medium rounded-sm mb-4">
              Who Is This For?
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-meritum-navy tracking-tight mb-6">
              Perfect for Emerging Skills
            </h2>
            <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed">
              MeritumPraxis is designed for individuals transitioning from formal learning 
              and training into professional environments.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {targetAudience.map((item, index) => (
              <div
                key={index}
                className="text-center p-8 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="w-16 h-16 bg-meritum-teal/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <item.icon className="text-meritum-teal" size={28} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-meritum-navy mb-3">
                  {item.title}
                </h3>
                <p className="font-body text-sm text-slate-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-3 py-1 bg-meritum-teal/10 text-meritum-teal font-body text-sm font-medium rounded-sm mb-4">
              Platform Features
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-meritum-navy tracking-tight mb-6">
              What You Can Do
            </h2>
            <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed">
              Our platform provides powerful tools to build and maintain your professional profile.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-slate-50 p-8 rounded-lg hover:bg-meritum-teal/5 transition-colors duration-300 group"
              >
                <div className="w-14 h-14 bg-meritum-gold/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-meritum-gold/20 transition-colors duration-300">
                  <feature.icon className="text-meritum-gold" size={24} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-meritum-navy mb-3">
                  {feature.title}
                </h3>
                <p className="font-body text-sm text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How Verification Works */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-3 py-1 bg-meritum-gold/10 text-meritum-gold font-body text-sm font-medium rounded-sm mb-4">
                Verification Process
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-meritum-navy tracking-tight mb-6">
                How Verification Works
              </h2>
              <p className="font-body text-base md:text-lg text-slate-600 leading-relaxed mb-8">
                Our verification process ensures that all achievements are credible and trustworthy.
              </p>
              <ul className="space-y-4">
                {[
                  'Performance claims supported by documented evidence',
                  'Referees verified by MeritumPraxis for credibility',
                  'Request referees to verify specific achievements',
                  'Referees receive secure tokenized verification links',
                  'All verifications are timestamped and recorded',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 size={20} className="text-meritum-gold mt-0.5 flex-shrink-0" />
                    <span className="font-body text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg border border-meritum-teal/10 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="text-meritum-gold" size={28} />
                <h3 className="font-heading text-xl font-semibold text-meritum-navy">
                  Trust & Credibility
                </h3>
              </div>
              <p className="font-body text-slate-600 leading-relaxed mb-6">
                Referees are verified for credibility, trust, and consistency. This ensures 
                that organizations can trust the information they see on your profile.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-meritum-gold/20 rounded-full flex items-center justify-center">
                    <BadgeCheck className="text-meritum-gold" size={20} />
                  </div>
                  <div>
                    <p className="font-body text-sm font-medium text-meritum-navy">Verified Achievement</p>
                    <p className="font-body text-xs text-slate-500">Confirmed by 2 referees</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                    Verified
                  </span>
                  <span className="px-2 py-1 bg-meritum-gold/10 text-meritum-gold text-xs font-medium rounded">
                    Timestamped
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Highlight */}
      <section className="py-16 bg-meritum-gold/10 border-y border-meritum-gold/20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-2xl font-semibold text-meritum-navy mb-2">
                Affordable Pricing
              </h3>
              <p className="font-body text-slate-600">
                Just <span className="font-bold text-meritum-teal">R35/month</span> or{' '}
                <span className="font-bold text-meritum-teal">R360/year</span> (save R60!)
              </p>
            </div>
            <Link to="/pricing">
              <Button className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold px-8 py-4">
                View Full Pricing
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-meritum-navy">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
            Start Building Your Profile Today
          </h2>
          <p className="font-body text-base md:text-lg text-slate-300 leading-relaxed mb-8 max-w-2xl mx-auto">
            Join the platform that makes your progress visible. Create your verified 
            professional profile and unlock new opportunities.
          </p>
          <Link to="/pricing">
            <Button 
              data-testid="candidates-cta-btn"
              className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold px-8 py-6 text-base"
            >
              Create Your Profile
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
